"use client";

import { cn } from "../../lib/utils";
import { motion, stagger, useAnimate, useInView } from "framer-motion";
import { useEffect } from "react";

export const TypewriterEffect = ({
  words,
  className,
  cursorClassName,
  repeat = Infinity,
  repeatDelay = 2,
}: {
  words: string[];
  className?: string;
  cursorClassName?: string;
  repeat?: number;
  repeatDelay?: number;
}) => {
  const [scope, animate] = useAnimate();
  const isInView = useInView(scope);

  useEffect(() => {
    if (isInView) {
      animate(
        "span",
        {
          opacity: 1,
          width: "fit-content",
        },
        {
          duration: 0.3,
          delay: stagger(0.1),
          ease: "easeInOut",
          repeat: repeat,
          repeatDelay: repeatDelay,
        }
      );
    }
  }, [isInView, animate, repeat, repeatDelay]);

  const renderWords = () => {
    return (
      <motion.div ref={scope} className="inline">
        {words.map((word, idx) => (
          <motion.span
            key={`word-${idx}`}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{
              duration: 0.3,
              delay: idx * 0.3,
              ease: "easeInOut",
            }}
            className={cn("dark:text-white text-black opacity-0", className)}
          >
            {word}&nbsp;
          </motion.span>
        ))}
      </motion.div>
    );
  };

  return (
    <div
      className={cn(
        "flex flex-col justify-center items-center text-center text-base sm:text-xl md:text-3xl lg:text-5xl font-bold",
        className
      )}
    >
      {renderWords()}
      <motion.span
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{
          duration: 0.8,
          repeat: Infinity,
          repeatType: "reverse",
        }}
        className={cn(
          "inline-block rounded-sm w-[4px] h-4 md:h-6 lg:h-10 bg-blue-500",
          cursorClassName
        )}
      ></motion.span>
    </div>
  );
};

export const TypewriterEffectSmooth = ({
  words,
  className,
  cursorClassName,
  fontSize,
}: {
  words: string[];
  className?: string;
  cursorClassName?: string;
  fontSize?: string;
}) => {
  const renderWords = () => {
    return (
      <div>
        {words.map((word, idx) => (
          <motion.span
            key={`word-${idx}`}
            initial={{ width: "0%" }}
            animate={{ width: "fit-content" }}
            transition={{
              duration: 0.5,
              ease: "easeInOut",
              delay: idx * 0.3,
              repeat: Infinity,
              repeatDelay: 1,
            }}
           
          >
            {word}&nbsp;
          </motion.span>
        ))}
      </div>
    );
  };

  return (
    <div
      className={cn(
        "flex justify-center items-center space-x-1 my-6 text-center",
        className
      )}
    >
      <motion.div
        className="overflow-hidden pb-2"
        initial={{ width: "0%" }}
        animate={{ width: "fit-content" }}
        transition={{
          duration: 2,
          ease: "linear",
          repeat: Infinity,
          repeatDelay: 1,
        }}
        style={{
          fontSize, 
        }}
      >
        <div
          className="text-xs sm:text-base md:text-xl lg:text:3xl xl:text-5xl font-bold"
          style={{ whiteSpace: "nowrap",fontSize,  }}
        >
          {renderWords()}
        </div>
      </motion.div>
      <motion.span
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{
          duration: 0.8,
          repeat: Infinity,
          repeatType: "reverse",
        }}
        className={cn(
          "block rounded-sm w-[4px] h-4 sm:h-6 xl:h-12 bg-blue-500",
          cursorClassName
        )}
      ></motion.span>
    </div>
  );
};
